import taskDataStore from './taskDataStore.js';
import { wrapResponse } from '../../utils/apiError.js';
import {
  maskTasksForPublic,
  toPublicTask,
  paginateItems,
  sortTasks,
  filterTasksByQuery,
  computeTaskStats,
  collectTags,
  isTaskOverdue,
} from '../../utils/taskHelpers.js';

const fetchPublicTasks = async () => {
  try {
    const items = await taskDataStore.getAll({ visibility: 'public' });
    return maskTasksForPublic(items);
  } catch (e) {
    return { error: e.message };
  }
};

const applyPublicFilters = (tasks, payload = {}) => {
  let filtered = [...tasks];
  if (payload.status) filtered = filtered.filter((t) => t.status === payload.status);
  if (payload.priority) filtered = filtered.filter((t) => t.priority === payload.priority);
  if (payload.tag) filtered = filtered.filter((t) => (t.tags || []).includes(payload.tag));
  if (payload.overdue) filtered = filtered.filter((t) => t.is_overdue);
  if (payload.q) filtered = filterTasksByQuery(filtered, payload.q);
  return filtered;
};

const TasksPublicServices = {
  getPublicItems: async (obj = {}) => {
    try {
      const tasks = await fetchPublicTasks();
      if (tasks.error) return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: tasks.error } });

      const filtered = applyPublicFilters(tasks, obj);
      const sorted = sortTasks(filtered, obj.sort || 'created_at', obj.order || 'desc');
      const { items, pagination } = paginateItems(sorted, obj);

      return wrapResponse({
        success: true,
        action: 'tasks.public.list',
        data: { items, pagination },
        summary: `${pagination.totalItems} public görev listelendi.`,
      });
    } catch (e) {
      return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: e.message } });
    }
  },

  getPublicItem: async (obj = {}) => {
    try {
      const result = await taskDataStore.getById(obj.id);
      if (!result) {
        return wrapResponse({ success: false, action: 'tasks.public.get', error: { code: 'NOT_FOUND', message: 'Görev bulunamadı' } });
      }
      if (result.visibility !== 'public') {
        return wrapResponse({ success: false, action: 'tasks.public.get', error: { code: 'NOT_FOUND', message: 'Görev bulunamadı' } });
      }
      const task = toPublicTask(result);
      return wrapResponse({ success: true, action: 'tasks.public.get', data: { task }, summary: `'${task.title}' detayı getirildi.` });
    } catch (e) {
      return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: e.message } });
    }
  },

  getPublicStats: async () => {
    try {
      const tasks = await fetchPublicTasks();
      if (tasks.error) return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: tasks.error } });
      const stats = computeTaskStats(tasks);
      return wrapResponse({ success: true, action: 'tasks.public.stats', data: { stats }, summary: `${stats.total} public görev.` });
    } catch (e) {
      return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: e.message } });
    }
  },

  searchPublicItems: async (obj = {}) => {
    try {
      const tasks = await fetchPublicTasks();
      if (tasks.error) return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: tasks.error } });

      const filtered = applyPublicFilters(tasks, obj);
      const sorted = sortTasks(filtered, obj.sort || 'created_at', obj.order || 'desc');
      const { items, pagination } = paginateItems(sorted, obj);

      return wrapResponse({
        success: true,
        action: 'tasks.public.search',
        data: { items, pagination },
        summary: `${pagination.totalItems} sonuç bulundu.`,
      });
    } catch (e) {
      return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: e.message } });
    }
  },

  getPublicTags: async () => {
    try {
      const tasks = await fetchPublicTasks();
      if (tasks.error) return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: tasks.error } });
      const tags = collectTags(tasks);
      return wrapResponse({ success: true, action: 'tasks.public.tags', data: { tags } });
    } catch (e) {
      return wrapResponse({ success: false, error: { code: 'INTERNAL_ERROR', message: e.message } });
    }
  },
};

export default TasksPublicServices;
